'use client';
import React, { useEffect, useRef, useState } from 'react';
import Icon from '@/components/ui/AppIcon';
import { useLanguage } from '@/components/language/LanguageProvider';

interface FormData {
  fullName: string;
  email: string;
  tattooIdea: string;
}

const INSTAGRAM_DM_URL = 'https://ig.me/m/juga_tattoo';

export default function BookingSection() {
  const revealRefs = useRef<(HTMLDivElement | null)[]>([]);
  const [formData, setFormData] = useState<FormData>({
    fullName: '',
    email: '',
    tattooIdea: '',
  });
  const [submitted, setSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const { dictionary } = useLanguage();

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) entry.target.classList.add('active');
        });
      },
      { threshold: 0.08 }
    );
    revealRefs.current.forEach((el) => {
      if (el) observer.observe(el);
    });
    return () => observer.disconnect();
  }, []);

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    if (submitError) {
      setSubmitError('');
    }
    setFormData((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitError('');
    setIsSubmitting(true);

    try {
      const response = await fetch('/api/booking', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      if (!response.ok) {
        const payload = (await response.json().catch(() => null)) as { error?: string } | null;
        throw new Error(payload?.error || dictionary.booking.submitError);
      }

      setSubmitted(true);
    } catch (error) {
      const message = error instanceof Error ? error.message : dictionary.booking.submitError;
      setSubmitError(message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const inputClass =
    'w-full bg-[#1A1A1A] border border-border rounded-xl px-4 py-3.5 text-foreground text-sm placeholder:text-muted-foreground focus:outline-none focus:border-primary/60 focus:ring-2 focus:ring-primary/20 transition-all duration-200';

  return (
    <section id="booking" className="relative bg-[#0D0D0D] py-20 overflow-hidden">
      {/* Atmospheric background */}
      <div className="absolute top-0 right-0 w-[700px] h-[700px] bg-primary/8 rounded-full blur-[140px] pointer-events-none translate-x-1/3 -translate-y-1/3" />
      <div className="absolute bottom-0 left-0 w-[600px] h-[600px] bg-primary/5 rounded-full blur-[120px] pointer-events-none -translate-x-1/3 translate-y-1/3" />
      {/* Noise texture */}
      <div className="absolute inset-0 bg-[url('https://grainy-gradients.vercel.app/noise.svg')] opacity-[0.04] mix-blend-soft-light pointer-events-none" />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="grid lg:grid-cols-12 gap-12 lg:gap-20 items-start">
          {/* Left: Value proposition */}
          <div className="lg:col-span-5 flex flex-col gap-8">
            <div
              ref={(el) => {
                revealRefs.current[0] = el;
              }}
              className="reveal"
            >
              <span className="inline-flex items-center gap-2 text-primary text-xs font-bold tracking-widest uppercase font-mono bg-primary/10 border border-primary/20 px-3 py-1.5 rounded-full mb-5">
                <span className="w-1.5 h-1.5 rounded-full bg-primary animate-pulse" />
                {dictionary.booking.badge}
              </span>
              <h2 className="text-4xl md:text-5xl font-extrabold text-foreground tracking-tight leading-tight mb-5">
                {dictionary.booking.titleLine1}
                <br />
                <span className="shimmer-gold">{dictionary.booking.titleLine2}</span>
              </h2>
              <p className="text-muted-foreground text-base leading-relaxed max-w-sm">
                {dictionary.booking.intro}
              </p>
            </div>

            {/* Trust indicators */}
            <div
              ref={(el) => {
                revealRefs.current[1] = el;
              }}
              className="reveal reveal-delay-1 flex flex-col gap-3"
            >
              {[
                { icon: 'CheckCircleIcon', text: dictionary.booking.trustItems[0] },
                { icon: 'ShieldCheckIcon', text: dictionary.booking.trustItems[1] },
                { icon: 'SparklesIcon', text: dictionary.booking.trustItems[2] },
                { icon: 'MapPinIcon', text: dictionary.booking.trustItems[3] },
              ].map((item) => (
                <div
                  key={item.text}
                  className="flex items-center gap-3 text-sm text-muted-foreground"
                >
                  <div className="w-8 h-8 rounded-xl bg-primary/10 border border-primary/20 flex items-center justify-center text-primary flex-shrink-0">
                    <Icon name={item.icon as 'CheckCircleIcon'} size={16} variant="outline" />
                  </div>
                  <span className="font-medium">{item.text}</span>
                </div>
              ))}
            </div>

          </div>

          {/* Right: Booking form */}
          <div className="lg:col-span-7">
            <div
              ref={(el) => {
                revealRefs.current[2] = el;
              }}
              className="reveal reveal-delay-1"
            >
              {submitted ? (
                <div className="bg-card border border-primary/30 rounded-3xl p-10 text-center gold-glow">
                  <div className="w-16 h-16 rounded-full bg-primary/20 border border-primary/30 flex items-center justify-center mx-auto mb-5">
                    <Icon name="CheckIcon" size={28} className="text-primary" />
                  </div>
                  <h3 className="text-foreground font-extrabold text-2xl mb-3">
                    {dictionary.booking.submittedTitle}
                  </h3>
                  <p className="text-muted-foreground text-base leading-relaxed max-w-sm mx-auto">
                    {dictionary.booking.submittedBody}
                  </p>
                  <a
                    href={INSTAGRAM_DM_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="mt-6 inline-flex items-center justify-center gap-2 rounded-full border border-primary/40 bg-primary/10 px-5 py-2.5 text-primary text-sm font-semibold hover:bg-primary/20 transition-colors"
                  >
                    <svg
                      width="16"
                      height="16"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <rect x="2" y="2" width="20" height="20" rx="5" ry="5" />
                      <circle cx="12" cy="12" r="4" />
                      <circle cx="17.5" cy="6.5" r="1" fill="currentColor" stroke="none" />
                    </svg>
                    {dictionary.booking.continueDm}
                  </a>
                </div>
              ) : (
                <div className="bg-card border border-border rounded-3xl p-8 md:p-10 relative overflow-hidden">
                  {/* Inner glow */}
                  <div className="absolute -top-20 -right-20 w-60 h-60 bg-primary/8 rounded-full blur-3xl pointer-events-none" />

                  <div className="relative z-10">
                    <h3 className="text-foreground font-extrabold text-2xl mb-1">
                      {dictionary.booking.formTitle}
                    </h3>
                    <p className="text-muted-foreground text-sm mb-5">
                      {dictionary.booking.formSubtitle}
                    </p>
                    <a
                      href={INSTAGRAM_DM_URL}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="mb-7 w-full inline-flex items-center justify-between gap-3 bg-primary text-primary-foreground pl-5 pr-3 py-3.5 rounded-xl font-bold text-sm hover:bg-primary/90 transition-all duration-300 hover:-translate-y-0.5 gold-glow"
                    >
                      <span className="inline-flex items-center gap-2">
                        <svg
                          width="16"
                          height="16"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="1.6"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        >
                          <rect x="2" y="2" width="20" height="20" rx="5" ry="5" />
                          <circle cx="12" cy="12" r="4" />
                          <circle cx="17.5" cy="6.5" r="1" fill="currentColor" stroke="none" />
                        </svg>
                        {dictionary.booking.dmButton}
                      </span>
                      <span className="bg-black/20 p-2 rounded-full">
                        <svg
                          width="13"
                          height="13"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2.5"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        >
                          <path d="M7 7h10v10M7 17 17 7" />
                        </svg>
                      </span>
                    </a>
                    <div className="mb-7 rounded-xl border border-border/80 bg-[#151515] px-4 py-3 text-xs text-muted-foreground">
                      {dictionary.booking.noInstagram}
                    </div>

                    <form onSubmit={handleSubmit} className="space-y-5">
                      {/* Row 1: Name + Email */}
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-xs font-bold text-foreground/70 uppercase tracking-wider mb-2">
                            {dictionary.booking.fullName} <span className="text-primary">*</span>
                          </label>
                          <input
                            type="text"
                            name="fullName"
                            required
                            value={formData.fullName}
                            onChange={handleChange}
                            placeholder={dictionary.booking.fullNamePlaceholder}
                            className={inputClass}
                          />
                        </div>
                        <div>
                          <label className="block text-xs font-bold text-foreground/70 uppercase tracking-wider mb-2">
                            {dictionary.booking.email} <span className="text-primary">*</span>
                          </label>
                          <input
                            type="email"
                            name="email"
                            required
                            value={formData.email}
                            onChange={handleChange}
                            placeholder={dictionary.booking.emailPlaceholder}
                            className={inputClass}
                          />
                        </div>
                      </div>

                      {/* Tattoo idea */}
                      <div>
                        <label className="block text-xs font-bold text-foreground/70 uppercase tracking-wider mb-2">
                          {dictionary.booking.tattooIdea} <span className="text-primary">*</span>
                        </label>
                        <textarea
                          name="tattooIdea"
                          required
                          rows={4}
                          value={formData.tattooIdea}
                          onChange={handleChange}
                          placeholder={dictionary.booking.tattooIdeaPlaceholder}
                          className={`${inputClass} resize-none`}
                        />
                      </div>

                      {/* Submit */}
                      <div className="pt-2">
                        {submitError && (
                          <p className="mb-3 rounded-xl border border-red-500/40 bg-red-500/10 px-4 py-3 text-sm text-red-300">
                            {submitError}
                          </p>
                        )}
                        <button
                          type="submit"
                          disabled={isSubmitting}
                          className="group w-full flex items-center justify-center gap-2 bg-primary text-primary-foreground py-4 rounded-xl font-bold text-sm hover:bg-primary/90 transition-all duration-300 hover:-translate-y-0.5 gold-glow"
                        >
                          {isSubmitting ? dictionary.booking.sending : dictionary.booking.submit}
                          <svg
                            width="16"
                            height="16"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2.5"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            className="group-hover:translate-x-1 transition-transform"
                          >
                            <path d="M5 12h14M12 5l7 7-7 7" />
                          </svg>
                        </button>
                        <p className="text-center text-xs text-muted-foreground mt-3">
                          {dictionary.booking.consent}
                        </p>
                      </div>
                    </form>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
